/**
 * computeIneffectiveActionRows / computeIneffectiveTotals — PMA-COCK-188/189
 * (client wave 2026-09-13).
 *
 * Lifted verbatim out of `MatchAnalytics.tsx`'s `analytics` useMemo: the
 * per-action "ineffectiveActionRows" table (formerly built inline around
 * L435-438/L732+ from `getIneffectiveAction`) plus the byAction-summed
 * "teamIneffectiveTotals" override used for the block's Total row (formerly
 * L440-461/L727-731). `MatchAnalytics` (PDF export table) and the relocated
 * `IneffectiveBreakdownSection` (on-screen "Tiempo perdido" block) both call
 * these two functions with the same resolved `IneffectiveBreakdown`, so the
 * two surfaces can never compute different numbers from the same input —
 * one source, no duplication.
 */
import type { IneffectiveBreakdown, IneffectiveAction } from "../utils";

export interface IneffectiveActionRow {
  action: IneffectiveAction;
  label: string;
  home: number;
  neutral: number;
  away: number;
}

export interface IneffectiveTotals {
  home: number;
  away: number;
  neutral: number;
}

type Translate = (key: string, fallback?: string) => string;

type ByAction = Record<
  IneffectiveAction,
  { home: number; away: number; neutral: number }
>;

const EMPTY_BY_ACTION = {} as ByAction;

/** Same fallback shape `MatchAnalytics.tsx`'s `analytics` useMemo used. */
function resolveTotals(
  ineffectiveBreakdown: IneffectiveBreakdown | null | undefined,
): { home: number; away: number; neutral: number; byAction: ByAction } {
  return (
    ineffectiveBreakdown?.totals ?? {
      home: 0,
      away: 0,
      neutral: 0,
      byAction: EMPTY_BY_ACTION,
    }
  );
}

/**
 * The per-action rows the "Tiempo perdido" block and the PDF's Ineffective
 * Breakdown table both render. Returns today's rows unchanged for the same
 * `ineffectiveBreakdown` input.
 */
export function computeIneffectiveActionRows(
  ineffectiveBreakdown: IneffectiveBreakdown | null | undefined,
  t: Translate,
): IneffectiveActionRow[] {
  const totals = resolveTotals(ineffectiveBreakdown);
  const getIneffectiveAction = (
    action: IneffectiveAction,
    team: "home" | "away" | "neutral",
  ) => totals.byAction?.[action]?.[team] ?? 0;

  return [
    {
      action: "Goal",
      label: t("analytics.ineffectiveGoal", "Gol"),
      home: getIneffectiveAction("Goal", "home"),
      neutral: getIneffectiveAction("Goal", "neutral"),
      away: getIneffectiveAction("Goal", "away"),
    },
    {
      action: "VAR",
      label: t("analytics.ineffectiveVar", "VAR"),
      home: getIneffectiveAction("VAR", "home"),
      neutral: getIneffectiveAction("VAR", "neutral"),
      away: getIneffectiveAction("VAR", "away"),
    },
    {
      action: "GoalKick",
      label: t("analytics.ineffectiveGoalKick", "Saque de puerta"),
      home: getIneffectiveAction("GoalKick", "home"),
      neutral: getIneffectiveAction("GoalKick", "neutral"),
      away: getIneffectiveAction("GoalKick", "away"),
    },
    {
      action: "FreeKick",
      label: t("analytics.ineffectiveFreeKick", "Tiros libres"),
      home: getIneffectiveAction("FreeKick", "home"),
      neutral: getIneffectiveAction("FreeKick", "neutral"),
      away: getIneffectiveAction("FreeKick", "away"),
    },
    {
      action: "ThrowIn",
      label: t("actionThrow-in", "Throw-in"),
      home: getIneffectiveAction("ThrowIn", "home"),
      neutral: getIneffectiveAction("ThrowIn", "neutral"),
      away: getIneffectiveAction("ThrowIn", "away"),
    },
    {
      action: "Corner",
      label: t("analytics.ineffectiveCorner", "Córner"),
      home: getIneffectiveAction("Corner", "home"),
      neutral: getIneffectiveAction("Corner", "neutral"),
      away: getIneffectiveAction("Corner", "away"),
    },
    {
      action: "Injury",
      label: t("analytics.ineffectiveInjury", "Lesión"),
      home: getIneffectiveAction("Injury", "home"),
      neutral: getIneffectiveAction("Injury", "neutral"),
      away: getIneffectiveAction("Injury", "away"),
    },
    {
      action: "Other",
      label: t("analytics.ineffectiveOther", "Otros"),
      home: getIneffectiveAction("Other", "home"),
      neutral: getIneffectiveAction("Other", "neutral"),
      away: getIneffectiveAction("Other", "away"),
    },
  ];
}

/**
 * Home/away/neutral totals for the "Tiempo perdido" block's Total row: sums
 * every byAction bucket except VAR, falling back to the breakdown's own
 * home/away totals when that sum is zero for both sides (e.g. a breakdown
 * that never populated byAction).
 */
export function computeIneffectiveTotals(
  ineffectiveBreakdown: IneffectiveBreakdown | null | undefined,
): IneffectiveTotals {
  const totals = resolveTotals(ineffectiveBreakdown);
  const summedTeamIneffective = Object.entries(totals.byAction || {}).reduce(
    (acc, [action, actionTotals]) => {
      if (action === "VAR") return acc;
      acc.home += actionTotals?.home ?? 0;
      acc.away += actionTotals?.away ?? 0;
      return acc;
    },
    { home: 0, away: 0 },
  );

  const hasSummedTotals =
    summedTeamIneffective.home > 0 || summedTeamIneffective.away > 0;

  return {
    home: hasSummedTotals ? summedTeamIneffective.home : totals.home,
    away: hasSummedTotals ? summedTeamIneffective.away : totals.away,
    neutral: totals.neutral,
  };
}
